import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ReceiptText, FileCheck2 } from "lucide-react";
import api from "../lib/api";
import { fmtMoney, fmtDate } from "../lib/format";
import DataTable from "../components/DataTable";
import StatusPill from "../components/StatusPill";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function ProvisionalDialog({ open, onClose }) {
  const qc = useQueryClient();
  const { data: scs = [] } = useQuery({ queryKey: ["sale-contracts"], queryFn: () => api.get("/sale-contracts") });
  const [sc, setSc] = useState("");
  const [amount, setAmount] = useState("");
  const chosen = scs.find((c) => c.id === sc);

  const save = async () => {
    if (!sc) return toast.error("Select a sale contract");
    await api.post("/invoices/provisional", { sale_contract_id: sc, amount: amount ? Number(amount) : undefined });
    toast.success("Provisional invoice raised — Dr AR / Cr Revenue posted");
    qc.invalidateQueries();
    setSc(""); setAmount(""); onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="provisional-dialog">
        <DialogHeader><DialogTitle>Raise Provisional Invoice</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Sale Contract</Label>
            <Select value={sc} onValueChange={setSc}>
              <SelectTrigger data-testid="prov-sc"><SelectValue placeholder="Select sale contract" /></SelectTrigger>
              <SelectContent>{scs.filter(c => c.status !== "Cancelled").map((c) => <SelectItem key={c.id} value={c.id}>{c.contract_ref} · {c.commodity} · {c.counterparty_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Provisional Amount (USD)</Label>
            <Input data-testid="prov-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder={chosen ? String(Math.round(chosen.quantity * chosen.price)) : "Defaults to contract value"} />
            <p className="text-xs text-slate-400 mt-1">Posts: Dr 1200 Accounts Receivable / Cr 4000 Sales Revenue</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="prov-save" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={save}>Raise Provisional</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FinalDialog({ open, onClose, provisional }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  React.useEffect(() => { if (provisional) setAmount(String(provisional.amount)); }, [provisional]);

  const save = async () => {
    await api.post("/invoices/final", { provisional_invoice_id: provisional.id, amount: Number(amount) });
    toast.success("Final invoice raised — provisional GL reversed & cleared into final AR");
    qc.invalidateQueries();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="final-dialog">
        <DialogHeader><DialogTitle>Raise Final Invoice</DialogTitle></DialogHeader>
        {provisional && (
          <div className="space-y-4">
            <div className="bg-slate-50 dark:bg-slate-800/40 rounded-md p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Provisional</span><span className="font-mono">{provisional.invoice_number}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Contract</span><span className="font-mono">{provisional.sale_contract_ref}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Provisional amount</span><span className="font-mono">{fmtMoney(provisional.amount)}</span></div>
            </div>
            <div>
              <Label>Final Amount (USD)</Label>
              <Input data-testid="final-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
              <p className="text-xs text-slate-400 mt-1">Reverses the provisional entry, then posts final Dr AR / Cr Revenue at this amount.</p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="final-save" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={save}>Raise Final & Clear</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Invoices() {
  const { data: invoices = [] } = useQuery({ queryKey: ["invoices"], queryFn: () => api.get("/invoices") });
  const [provOpen, setProvOpen] = useState(false);
  const [finalFor, setFinalFor] = useState(null);

  const cols = [
    { key: "invoice_number", label: "Invoice #", render: (r) => <span className="font-mono text-xs font-semibold">{r.invoice_number}</span> },
    { key: "type", label: "Type", render: (r) => <StatusPill status={r.type === "Final" ? "Confirmed" : "Draft"} /> },
    { key: "sale_contract_ref", label: "Sale Contract" },
    { key: "counterparty_name", label: "Buyer" },
    { key: "commodity", label: "Commodity", render: (r) => <span className="uppercase text-xs font-semibold">{r.commodity}</span> },
    { key: "amount", label: "Amount", align: "right", render: (r) => fmtMoney(r.amount) },
    { key: "invoice_date", label: "Date", render: (r) => <span className="text-xs">{fmtDate(r.invoice_date)}</span> },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status === "Open" ? "Open" : r.status === "Finalised" ? "Closed" : "Settled"} /> },
    { key: "_a", label: "Actions", render: (r) => (
      r.type === "Provisional" && r.status === "Open"
        ? <Button data-testid={`finalise-${r.invoice_number}`} size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setFinalFor(r); }}>
            <FileCheck2 className="h-4 w-4 mr-1" /> Finalise
          </Button>
        : <span className="text-xs text-slate-400">—</span>
    ) },
  ];

  return (
    <div data-testid="invoices-page">
      <DataTable
        testid="invoices-table"
        title="Invoice Workflow — Provisional → Final"
        columns={cols}
        rows={invoices}
        csvName="invoices"
        onAdd={() => setProvOpen(true)}
        addLabel="Provisional Invoice"
      />
      <ProvisionalDialog open={provOpen} onClose={() => setProvOpen(false)} />
      <FinalDialog open={!!finalFor} onClose={() => setFinalFor(null)} provisional={finalFor} />
    </div>
  );
}
